/**
 * Extension Detection Utilities
 * 
 * Single source of truth for all wallet detection logic.
 * Provides reusable wallet detection functions that can be shared across components.
 * 
 * This module consolidates all wallet detection logic from useAgentDelegation.ts
 * for better separation of concerns and maintainability.
 */

import type { UnisatProvider, OKXBitcoinProvider } from "../signing/bitcoin";
import {
  isOKXEthereumAvailable as isOKXEthereumSDKAvailable,
  isOKXSolanaAvailable as isOKXSolanaSDKAvailable,
  isOKXBitcoinAvailable as isOKXBitcoinSDKAvailable,
} from "../signing/okx";
import {
  isOneKeySolanaAvailable as isOneKeySolanaSDKAvailable,
  isOneKeyBitcoinAvailable as isOneKeyBitcoinSDKAvailable,
} from "../signing/onekey";
import {
  isBinanceSolanaAvailable,
  isBitgetSolanaAvailable,
} from "../signing/solana";
import {
  isBitcoinWalletAvailable,
} from "../signing/bitcoin";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Supported wallet types (lowercase for compatibility with existing code)
 */
export type SupportedWallet = "metamask" | "phantom" | "onekey" | "okx" | "binance" | "bitget" | "tokenpocket" | "coinbase" | "rainbow";

/**
 * Chain types for wallet detection
 */
export type ChainType = "ethereum" | "solana" | "bitcoin";

/**
 * Wallet names (display names, capitalized)
 */
export type WalletName =
  | "OneKey"
  | "OKX"
  | "Bitget"
  | "TokenPocket"
  | "Coinbase Wallet"
  | "Rainbow"
  | "Binance"
  | "MetaMask"
  | "Phantom"
  | "Bitcoin Wallet"
  | null;

/**
 * Type declaration for Ethereum provider
 */
export interface EthereumProvider {
  request(args: { method: "eth_requestAccounts" }): Promise<string[]>;
  request(args: {
    method: "eth_signTypedData_v4";
    params: [string, string];
  }): Promise<string>;
  isMetaMask?: boolean;
  isPhantom?: boolean;
  isOneKey?: boolean;
  isBinance?: boolean;
  isBitEthereum?: boolean; // Bitget Ethereum flag
  isTokenPocket?: boolean; // TokenPocket Ethereum flag
  isCoinbaseWallet?: boolean; // Coinbase Wallet Ethereum flag
  isWalletLink?: boolean; // Legacy WalletLink flag (Coinbase's old bridging tech)
  isRainbow?: boolean; // Rainbow Wallet Ethereum flag
  isOKX?: boolean; // OKX Ethereum flag
  host?: string; // Coinbase Wallet host URL
  jsonRpcUrl?: string; // Coinbase Wallet JSON-RPC URL
  overridesMetaMask?: boolean; // Coinbase Wallet override flag
  providers?: EthereumProvider[];
  providerName?: string;
  name?: string;
  _state?: { name?: string };
  _metamask?: any;
  _postMessageToMp?: () => void;
  namespace?: string;
  providerInfo?: {
    rdns?: string;
    name?: string;
    uuid?: string;
  };
}

/**
 * Type declaration for Phantom wallet (Ethereum provider)
 */
export interface PhantomProvider {
  ethereum?: EthereumProvider;
  isPhantom?: boolean;
}

/**
 * Type declaration for Phantom Solana provider
 */
export interface PhantomSolanaProvider {
  isPhantom?: boolean;
  isConnected: boolean;
  publicKey: { toString(): string } | null;
  connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
  disconnect(): Promise<void>;
  signMessage(
    message: Uint8Array | string,
    display?: "utf8" | "hex"
  ): Promise<{ signature: Uint8Array; publicKey: { toString(): string } }>;
}

/**
 * Type declaration for Bitget Wallet (multi-chain wallet)
 */
export interface BitgetProvider {
  isBitKeep?: boolean; // Legacy BitKeep flag (still used by Bitget)
  ethereum?: EthereumProvider & { isBitEthereum?: boolean };
  solana?: {
    isBitKeep?: boolean;
    connect(opts?: { onlyIfTrusted?: boolean }): Promise<{ publicKey: { toString(): string } }>;
    disconnect(): Promise<void>;
    signMessage(message: Uint8Array | string, display?: "utf8" | "hex"): Promise<{ signature: Uint8Array; publicKey: { toString(): string } }>;
    publicKey: { toString(): string } | null;
    isConnected: boolean;
  };
  unisat?: {
    // Bitcoin provider (Bitget uses unisat namespace for Bitcoin)
    setIdenties(accounts: string[]): void;
    useIdentity(address: string): void;
    changeAddress(address: string): void;
    requestAccounts(): Promise<string[]>;
    signMessage(message: string): Promise<string>;
  };
  tronLink?: any;
  suiWallet?: any;
  aptos?: any;
}

/**
 * Global Window interface extensions for wallet providers
 */
declare global {
  interface Window {
    ethereum?: EthereumProvider;
    phantom?: PhantomProvider;
    solana?: PhantomSolanaProvider; // Phantom Solana provider
    // $onekey type must match bitcoin.ts declaration exactly
    $onekey?: {
      ethereum?: any; // OneKey Ethereum provider (EthereumProvider type, but using any for compatibility)
      solana?: any; // OneKey Solana provider
      btc?: UnisatProvider; // OneKey Bitcoin provider (must match bitcoin.ts)
    };
    // okxwallet type must match bitcoin.ts declaration exactly, but we add solana for Solana support
    okxwallet?: {
      ethereum?: any; // OKX Ethereum provider (EthereumProvider, but using any for compatibility)
      solana?: any; // OKX Solana provider (added here, not in bitcoin.ts)
      bitcoin?: OKXBitcoinProvider; // OKX Bitcoin provider (must match bitcoin.ts)
    };
    BinanceChain?: EthereumProvider; // Binance Chain wallet (legacy)
    binancew3w?: {
      isExtension?: boolean;
      solana?: any;
    };
    bitkeep?: BitgetProvider; // Bitget Wallet (formerly BitKeep)
    tokenpocket?: any; // TokenPocket root namespace indicator (primary detection method)
    tp?: any; // TokenPocket legacy namespace (older versions)
    CoinbaseWalletProvider?: any; // Coinbase Wallet provider class (injected into window)
    WalletLinkProvider?: any; // Legacy WalletLink provider class (Coinbase's old bridging tech)
    coinbaseSolana?: {
      _storage?: {
        scope?: string; // Should be 'coinbaseSolana' for genuine Coinbase
      };
      storage?: {
        scope?: string;
      };
      connect?: () => Promise<any>;
      disconnect?: () => Promise<void>;
      signMessage?: () => Promise<any>;
      signTransaction?: () => Promise<any>;
    };
    unisat?: UnisatProvider; // Bitcoin wallet (Unisat)
  }
}

// ============================================================================
// BASIC WALLET DETECTION FUNCTIONS
// ============================================================================

/**
 * Detects if OneKey wallet is available
 */
export function isOneKeyAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return (
    !!(window as any).$onekey?.ethereum ||
    !!(window as any).ethereum?.isOneKey ||
    !!(window as any).$onekey
  );
}

/**
 * Detects if Phantom wallet is available (Ethereum)
 * Excludes OneKey masquerading as Phantom
 */
export function isPhantomAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return (
    (!!(window as any).phantom?.ethereum ||
      !!(window as any).ethereum?.isPhantom) &&
    // Exclude if it's actually OneKey (OneKey provides window.phantom for backward compatibility)
    !((window as any).$onekey || (window as any).ethereum?.isOneKey)
  );
}

/**
 * Detects if OKX Ethereum wallet is available
 */
export function isOKXEthereumAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return (
    !!(window as any).okxwallet?.ethereum ||
    !!(window as any).ethereum?.isOKX
  );
}

/**
 * Detects if Rainbow wallet is available
 */
export function isRainbowAvailable(): boolean {
  if (typeof window === "undefined") return false;
  return (window as any).ethereum?.isRainbow === true;
}

// ============================================================================
// METAMASK DETECTION
// ============================================================================

/**
 * Check if MetaMask wallet provider is truly available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually MetaMask and not a fake or another wallet
 * 
 * MetaMask injects:
 * - window.ethereum with isMetaMask === true
 * - window.ethereum._metamask (internal object)
 * - window.ethereum.providerInfo.rdns === "io.metamask"
 * - window.ethereum.providerInfo.uuid (v4 UUID)
 * - window.ethereum.providerInfo.name === "MetaMask"
 * - window.solana.isMetaMask === true (if Solana support enabled)
 * 
 * Detection methods (in order of reliability):
 * 1. isMetaMask flag (primary check)
 * 2. _metamask internal object
 * 3. providerInfo.rdns === "io.metamask"
 * 4. providerInfo.name === "MetaMask"
 * 5. UUID presence (v4 format)
 * 6. Solana provider with MetaMask branding
 */
export function isMetaMaskProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // Method 1: Primary check - isMetaMask flag (most reliable)
  // This is set by initializeProvider from @metamask/providers
  if (ethereum.isMetaMask === true) {
    // Additional verification to ensure it's not a fake
    // Check for _metamask internal object (Method 2)
    if (ethereum._metamask && typeof ethereum._metamask === "object") {
      return true;
    }
    
    // Check for providerInfo.rdns (Method 3)
    if (ethereum.providerInfo?.rdns === "io.metamask") {
      return true;
    }
    
    // Check for providerInfo.name (Method 4)
    if (ethereum.providerInfo?.name === "MetaMask") {
      return true;
    }
    
    // Check for UUID presence (Method 5)
    // MetaMask generates a v4 UUID for each page load
    if (ethereum.providerInfo?.uuid && 
        typeof ethereum.providerInfo.uuid === "string" &&
        ethereum.providerInfo.uuid.length > 0) {
      return true;
    }
    
    // If isMetaMask is true and we don't have conflicting wallets, assume it's MetaMask
    // But exclude known conflicting wallets
    const hasOKX = !!(window as any).okxwallet;
    const hasBinance = isBinanceProviderAvailable();
    const hasOneKey = !!(window as any).$onekey || ethereum.isOneKey;
    const hasPhantom = !!(window as any).phantom?.ethereum || ethereum.isPhantom;
    
    // Only return true if no conflicting wallets are detected
    if (!hasOKX && !hasBinance && !hasOneKey && !hasPhantom) {
      return true;
    }
  }
  
  // Method 6: Check Solana provider with MetaMask branding
  const solana = (window as any).solana;
  if (solana?.isMetaMask === true) {
    return true;
  }
  
  return false;
}

/**
 * Async version that queries wallet_getProviderInfo for additional verification
 * This provides the most reliable detection but requires an async call
 * 
 * @returns Promise that resolves to true if MetaMask is detected, false otherwise
 */
export async function verifyMetaMaskProvider(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // First do synchronous checks
  if (!isMetaMaskProviderAvailable()) {
    return false;
  }
  
  // Additional async verification via wallet_getProviderInfo
  try {
    const providerInfo = await ethereum.request({ 
      method: 'wallet_getProviderInfo' 
    });
    
    if (providerInfo && typeof providerInfo === "object") {
      // Verify it's MetaMask
      if (providerInfo.name === "MetaMask" || 
          providerInfo.rdns === "io.metamask") {
        return true;
      }
    }
  } catch (error) {
    // If the method is not supported, fall back to synchronous checks
    // This is fine - not all MetaMask versions support wallet_getProviderInfo
    console.debug("wallet_getProviderInfo not available, using synchronous checks");
  }
  
  // Fall back to synchronous check result
  return isMetaMaskProviderAvailable();
}

// ============================================================================
// BINANCE DETECTION
// ============================================================================

/**
 * Check if Binance wallet provider is available (immediate check)
 * Uses inpage detection with multiple fallback methods
 * 
 * Modern Binance Web3 Wallet injects:
 * - window.binancew3w = { isExtension: true }
 * - window.ethereum (with Binance-specific properties)
 * - window.solana, window.binancew3w.tron, window.sui, etc.
 * 
 * Legacy Binance Chain Wallet injects:
 * - window.BinanceChain
 */
export function isBinanceProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Method 1: Modern Binance Web3 Wallet - Primary detection
  // Check for window.binancew3w.isExtension flag (from inpage script)
  if ((window as any).binancew3w?.isExtension === true) {
    return true;
  }
  
  // Method 2: Legacy BinanceChain (still used by older versions)
  if ((window as any).BinanceChain) {
    return true;
  }
  
  // Method 3: window.ethereum with isBinance flag
  const ethereum = (window as any).ethereum;
  if (ethereum?.isBinance) {
    return true;
  }
  
  // Method 4: Check window.ethereum provider name/label for "Binance"
  if (ethereum) {
    // Check if provider name contains "Binance"
    const providerName = ethereum.providerName || ethereum.name || 
                         (ethereum._state?.name) || 
                         (ethereum._metamask?.isUnlocked === undefined ? "Binance" : null);
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return true;
    }
  }
  
  // Method 5: Fallback - Check for Binance-specific properties
  // Binance wallet injects these properties even when isBinance is false
  if (ethereum?._postMessageToMp && 
      typeof ethereum._postMessageToMp === "function" &&
      ethereum?.namespace === "eip155") {
    return true;
  }
  
  // Method 6: Check for Binance Solana provider
  // Modern Binance injects window.solana or window.binancew3w.solana
  if ((window as any).binancew3w?.isExtension === true && (window as any).binancew3w?.solana) {
    return true;
  }
  
  const solana = (window as any).solana;
  if (solana) {
    // Check for Binance-specific flags
    if (solana.isBinance === true) {
      return true;
    }
    // Check provider name for "Binance"
    const providerName = solana.name || solana.providerName;
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return true;
    }
    // If binancew3w.isExtension exists, window.solana is likely Binance
    if ((window as any).binancew3w?.isExtension === true) {
      return true;
    }
  }
  
  // Method 7: Check window.ethereum.solana (newer format)
  if ((window as any).ethereum?.solana) {
    return true;
  }
  
  // Method 8: Check window.BinanceChain.solana (legacy)
  if ((window as any).BinanceChain?.solana) {
    return true;
  }
  
  return false;
}

/**
 * Detects if Binance wallet is available (basic check)
 * Note: For comprehensive detection with polling, use waitForBinanceWallet
 */
export function isBinanceAvailable(): boolean {
  return isBinanceProviderAvailable();
}

/**
 * Wait for Binance wallet to become available (inpage detection with polling)
 * Handles timing issues where Binance extension injects window.BinanceChain asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Binance is detected, false if timeout
 */
export async function waitForBinanceWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isBinanceProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isBinanceProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  // Binance injects at document_start but may not be immediately available
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Binance wallet
      if (isBinanceProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Get Binance wallet provider (with inpage detection)
 * Returns the provider object if available, null otherwise
 * 
 * Priority order:
 * 1. window.binancew3w.isExtension (modern) -> use window.ethereum
 * 2. window.BinanceChain (legacy)
 * 3. window.ethereum with Binance detection
 */
export function getBinanceProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  // Method 1: Modern Binance Web3 Wallet
  // If binancew3w.isExtension exists, use window.ethereum (it's injected by Binance)
  if ((window as any).binancew3w?.isExtension === true) {
    const ethereum = (window as any).ethereum;
    if (ethereum) {
      return ethereum as EthereumProvider;
    }
  }
  
  // Method 2: Legacy BinanceChain (older versions)
  if ((window as any).BinanceChain) {
    return (window as any).BinanceChain as EthereumProvider;
  }
  
  // Method 3: window.ethereum with Binance detection
  const ethereum = (window as any).ethereum;
  if (ethereum) {
    // Check isBinance flag
    if (ethereum.isBinance) {
      return ethereum as EthereumProvider;
    }
    
    // Check provider name/label
    const providerName = ethereum.providerName || ethereum.name || 
                         (ethereum._state?.name);
    if (typeof providerName === "string" && providerName.toLowerCase().includes("binance")) {
      return ethereum as EthereumProvider;
    }
    
    // Check Binance-specific properties
    if (ethereum._postMessageToMp && 
        typeof ethereum._postMessageToMp === "function" &&
        ethereum?.namespace === "eip155") {
      return ethereum as EthereumProvider;
    }
  }
  
  return null;
}

// ============================================================================
// BITGET DETECTION
// ============================================================================

/**
 * Check if Bitget Wallet provider is available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually Bitget Wallet
 * 
 * Bitget Wallet (formerly BitKeep) injects:
 * - window.bitkeep.isBitKeep === true (primary flag)
 * - window.bitkeep.ethereum.isBitEthereum === true (Ethereum-specific flag, primary for Ethereum detection)
 * - window.bitkeep namespace with multiple sub-providers (unique to Bitget)
 * - window.bitkeep.solana (Solana provider)
 * - window.bitkeep.unisat (Bitcoin provider, primary)
 * - window.unisat (Bitcoin provider shim, optional - only if Bitget is detected via other means)
 * - window.bitkeep.tronLink (Tron provider)
 * - window.bitkeep.suiWallet, window.bitkeep.aptos, etc. (other chains)
 * 
 * Detection methods (in order of reliability):
 * 1. window.bitkeep.isBitKeep === true (primary check for general Bitget detection)
 * 2. window.bitkeep.ethereum.isBitEthereum === true (Ethereum-specific, primary for Ethereum)
 * 3. window.bitkeep namespace exists with multiple providers (multi-chain indicator)
 * 4. window.bitkeep.solana with isBitKeep flag or connect function
 * 
 * Based on analysis of bitget-inpage.txt:
 * - Root object: window.bitkeep = {} (main namespace)
 * - Ethereum: window.bitkeep.ethereum with isBitEthereum: true flag
 * - Solana: window.bitkeep.solana (Solana-compatible provider)
 * - Bitcoin: window.bitkeep.unisat (primary), may also shim window.unisat
 */
export function isBitgetProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return false;
  
  // Method 1: Primary check - isBitKeep flag (most reliable general detection)
  // This is the official detection flag per Bitget docs
  if (bitkeep.isBitKeep === true) {
    return true;
  }
  
  // Method 2: Ethereum-specific flag (primary for Ethereum detection)
  // Per analysis: window.bitkeep.ethereum.isBitEthereum === true is the key Ethereum indicator
  if (bitkeep.ethereum?.isBitEthereum === true) {
    return true;
  }
  
  // Method 3: Check for multiple sub-providers (unique to Bitget)
  // Bitget is the only wallet that uses window.bitkeep with multiple providers
  // This is a strong indicator of Bitget (multi-chain wallet)
  const hasEthereum = !!bitkeep.ethereum && typeof bitkeep.ethereum.request === "function";
  const hasSolana = !!bitkeep.solana && typeof bitkeep.solana.connect === "function";
  const hasUnisat = !!bitkeep.unisat; // Bitcoin provider
  const hasTronLink = !!bitkeep.tronLink;
  
  // If we have multiple providers under bitkeep namespace, it's very likely Bitget
  const providerCount = [hasEthereum, hasSolana, hasUnisat, hasTronLink].filter(Boolean).length;
  if (providerCount >= 2) {
    return true;
  }
  
  // Method 4: Check Solana provider with isBitKeep flag
  if (bitkeep.solana?.isBitKeep === true) {
    return true;
  }
  
  // Method 5: Fallback - if bitkeep namespace exists with ethereum provider
  // and no conflicting wallets are detected, assume it's Bitget
  if (hasEthereum) {
    // Exclude known conflicting wallets
    const hasOKX = !!(window as any).okxwallet;
    const hasBinance = isBinanceProviderAvailable();
    const hasOneKey = !!(window as any).$onekey;
    const hasPhantom = !!(window as any).phantom?.ethereum;
    const hasMetaMask = isMetaMaskProviderAvailable();
    
    // Only return true if no conflicting wallets are detected
    if (!hasOKX && !hasBinance && !hasOneKey && !hasPhantom && !hasMetaMask) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get Bitget Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 * 
 * Per analysis: window.bitkeep.ethereum with isBitEthereum: true flag
 * Supports EIP-1193: request({ method: 'eth_requestAccounts' })
 * Connection state: window.bitkeep.ethereum.selectedAddress and isConnected()
 */
export function getBitgetProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const bitkeep = (window as any).bitkeep;
  if (!bitkeep) return null;
  
  // Check if it's actually Bitget
  if (!isBitgetProviderAvailable()) {
    return null;
  }
  
  // Return Ethereum provider
  // Per analysis: window.bitkeep.ethereum with isBitEthereum flag
  if (bitkeep.ethereum && typeof bitkeep.ethereum.request === "function") {
    // Verify it has the Bitget Ethereum flag
    if (bitkeep.ethereum.isBitEthereum === true || bitkeep.isBitKeep === true) {
      return bitkeep.ethereum as EthereumProvider;
    }
  }
  
  return null;
}

/**
 * Wait for Bitget wallet to become available (inpage detection with polling)
 * Handles timing issues where Bitget extension injects window.bitkeep asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Bitget is detected, false if timeout
 */
export async function waitForBitgetWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isBitgetProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isBitgetProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Bitget wallet
      if (isBitgetProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

/**
 * Detects if Bitget Bitcoin wallet is available
 */
export function isBitgetBitcoinAvailable(): boolean {
  if (typeof window === "undefined") return false;
  const bitkeep = (window as any).bitkeep;
  const hasBitgetWallet = !!(
    bitkeep?.isBitKeep || bitkeep?.ethereum?.isBitEthereum
  );
  // Primary: Check window.bitkeep.unisat
  // Secondary: Check window.unisat shim (only if Bitget is detected and not OneKey)
  return (
    hasBitgetWallet &&
    (!!bitkeep?.unisat ||
      (!(window as any).$onekey && !!(window as any).unisat))
  );
}

// ============================================================================
// TOKENPOCKET DETECTION
// ============================================================================

/**
 * Check if TokenPocket Wallet provider is available (comprehensive detection)
 * Uses multiple verification methods to ensure it's actually TokenPocket Wallet
 * 
 * TokenPocket Wallet is a multi-chain wallet that injects providers for:
 * - Ethereum: window.ethereum.isTokenPocket === true (primary flag, official detection method)
 * - Solana: window.solana (when TokenPocket is detected via window.tokenpocket or ethereum.isTokenPocket)
 * - Other chains: Tron, EOS, etc. (via various namespaces)
 * 
 * Detection methods (in order of reliability):
 * 1. window.tokenpocket exists (root namespace indicator - most reliable for multi-chain detection)
 * 2. window.ethereum.isTokenPocket === true (primary check, official flag)
 * 3. window.tp exists (legacy namespace check - TokenPocket was formerly TP Wallet)
 * 4. window.ethereum with TokenPocket-specific properties
 * 
 * Note: This function checks for Ethereum provider availability.
 * For Solana, use isTokenPocketSolanaAvailable().
 */
export function isTokenPocketProviderAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  // Method 1: Primary check - window.tokenpocket root namespace (most reliable indicator)
  // TokenPocket injects window.tokenpocket as a root namespace indicator
  if (typeof (window as any).tokenpocket !== "undefined") {
    return true;
  }
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  // Method 2: Check - isTokenPocket flag (official detection flag per TokenPocket docs)
  if (ethereum.isTokenPocket === true) {
    return true;
  }
  
  // Method 3: Legacy namespace check (window.tp)
  // TokenPocket was formerly known as TP Wallet
  if ((window as any).tp && typeof (window as any).tp === "object") {
    return true;
  }
  
  // Method 4: Check for TokenPocket-specific properties
  // TokenPocket may set additional properties on ethereum provider
  if (ethereum.isTokenPocket !== undefined) {
    return true;
  }
  
  // Method 4: Multi-chain heuristic
  // TokenPocket is a multi-chain wallet, so if we detect multiple chain providers
  // and no other known wallet is detected, it might be TokenPocket
  const hasSolana = !!(window as any).solana;
  const hasUnisat = !!(window as any).unisat;
  const hasMultipleChains = hasSolana && hasUnisat;
  
  // Exclude known conflicting wallets
  const hasOKX = !!(window as any).okxwallet;
  const hasBinance = isBinanceProviderAvailable();
  const hasOneKey = !!(window as any).$onekey;
  const hasPhantom = !!(window as any).phantom?.ethereum;
  const hasMetaMask = isMetaMaskProviderAvailable();
  const hasBitget = isBitgetProviderAvailable();
  
  // If multiple chains detected and no other wallet is present, might be TokenPocket
  // But we need to be careful - only return true if we have strong evidence
  if (hasMultipleChains && !hasOKX && !hasBinance && !hasOneKey && !hasPhantom && !hasMetaMask && !hasBitget) {
    // Additional check: verify it's not just a generic multi-chain setup
    // TokenPocket typically injects window.ethereum with isTokenPocket
    if (ethereum && typeof ethereum.request === "function") {
      // Weak signal, but if no other wallet detected, might be TokenPocket
      return false; // Too weak, don't assume
    }
  }
  
  return false;
}

/**
 * Check if TokenPocket supports Ethereum chain
 * @returns true if TokenPocket Ethereum provider is available
 */
export function isTokenPocketEthereumAvailable(): boolean {
  return isTokenPocketProviderAvailable();
}

/**
 * Check if TokenPocket supports Solana chain
 * @returns true if TokenPocket Solana provider is available
 * 
 * Based on TokenPocket SDK analysis:
 * - TokenPocket injects window.tokenpocket as root namespace
 * - Uses window.solana for Solana provider (similar to Phantom)
 * - Can check tp.isConnected() if window.tp exists (SDK bridge check)
 */
export function isTokenPocketSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const solana = (window as any).solana;
  if (!solana) return false;
  
  // Method 1: PRIMARY - Check window.solana.name property (simplest and most direct)
  // TokenPocket sets name: 'TokenPocket' on the solana provider (confirmed via console inspection)
  // IMPORTANT: Check this FIRST before excluding Phantom, because TokenPocket ALSO sets isPhantom: true for compatibility!
  if (solana.name === 'TokenPocket') {
    return true; // TokenPocket detected via name property (even if isPhantom: true)
  }
  
  // Method 1b: Check features.name (Wallet Standard property)
  // TokenPocket also sets features.name: 'TokenPocket' in the Wallet Standard features object
  if (solana.features && (solana.features as any).name === 'TokenPocket') {
    return true; // TokenPocket detected via features.name (even if isPhantom: true)
  }
  
  // Method 1c: Fallback - Check for TokenPocket private field #e: 'TokenPocket' via object inspection
  // TokenPocket sets a private field #e with value 'TokenPocket' on the solana provider
  try {
    const providerString = String(solana);
    const providerValues = Object.values(solana);
    
    if (providerString.includes('TokenPocket') || 
        providerValues.some((v: any) => String(v) === 'TokenPocket')) {
      return true; // TokenPocket detected via #e field (even if isPhantom: true)
    }
    
    // Check for any symbol that might expose the private field
    const symbols = Object.getOwnPropertySymbols(solana);
    for (const sym of symbols) {
      const value = (solana as any)[sym];
      if (value === 'TokenPocket' || String(value) === 'TokenPocket') {
        return true; // TokenPocket detected (even if isPhantom: true)
      }
    }
  } catch (error) {
    // Ignore errors from inspection attempts
  }
  
  // Exclude Phantom - BUT only if we haven't detected TokenPocket via #e field above
  // TokenPocket sets isPhantom: true for compatibility, so we must check #e first
  if (solana.isPhantom === true) return false;
  
  const tokenpocket = (window as any).tokenpocket;
  const ethereum = (window as any).ethereum;
  const tp = (window as any).tp;
  
  // Method 2: Check - window.tokenpocket root namespace (most reliable)
  // TokenPocket injects window.tokenpocket as a root namespace indicator
  if (typeof tokenpocket !== "undefined") {
    return true;
  }
  
  // Method 3: Check tp.isConnected() if window.tp exists (SDK bridge check)
  // From SDK: tp.isConnected() checks for TPJSBrigeClient or webkit messageHandlers
  if (tp && typeof tp.isConnected === "function") {
    try {
      if (tp.isConnected()) {
        return true;
      }
    } catch (error) {
      // isConnected() may throw in some contexts, ignore
    }
  }
  
  // Method 4: Fallback - Check for TokenPocket Ethereum flag
  if (ethereum?.isTokenPocket === true) {
    return true;
  }
  
  // Method 5: Legacy check - window.tp namespace (older TokenPocket versions)
  if (tp && typeof tp === "object") {
    return true;
  }
  
  return false;
}

/**
 * Get TokenPocket Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 */
export function getTokenPocketProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return null;
  
  // Check if it's actually TokenPocket
  if (!isTokenPocketProviderAvailable()) {
    return null;
  }
  
  // Return Ethereum provider
  if (ethereum && typeof ethereum.request === "function") {
    return ethereum as EthereumProvider;
  }
  
  return null;
}

/**
 * Wait for TokenPocket wallet to become available (inpage detection with polling)
 * Handles timing issues where TokenPocket extension injects window.ethereum asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if TokenPocket is detected, false if timeout
 */
export async function waitForTokenPocketWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isTokenPocketProviderAvailable()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isTokenPocketProviderAvailable()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for TokenPocket wallet
      if (isTokenPocketProviderAvailable()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

// ============================================================================
// COINBASE WALLET DETECTION
// ============================================================================

/**
 * Check if Coinbase Wallet provider is available (robust multi-check detection)
 * Uses multiple verification methods to ensure it's actually Coinbase Wallet and not a fake
 * 
 * Coinbase Wallet injects:
 * - window.ethereum with isCoinbaseWallet === true
 * - window.ethereum.host (must include 'coinbase.com' domain)
 * - window.ethereum.jsonRpcUrl (must include 'coinbase.com' domain)
 * - window.ethereum.isWalletLink === true (legacy flag, true in genuine installs)
 * - window.ethereum.overridesMetaMask === false (genuine doesn't override MetaMask)
 * - window.CoinbaseWalletProvider (injected class)
 * - window.WalletLinkProvider (legacy class)
 * - window.solana with isCoinbaseWallet === true (for Solana support)
 * - window.coinbaseSolana.storage.scope === 'coinbaseSolana' (for Solana)
 * 
 * Detection methods (in order of reliability):
 * 1. isCoinbaseWallet flag (primary check)
 * 2. host property includes 'coinbase.com'
 * 3. jsonRpcUrl property includes 'coinbase.com'
 * 4. isWalletLink flag (legacy, true in genuine)
 * 5. overridesMetaMask === false
 * 6. CoinbaseWalletProvider class exists
 * 7. WalletLinkProvider class exists
 * 8. Behavioral check: Test RPC call (eth_chainId)
 */
export async function isCoinbaseWalletProviderAvailable(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  let provider = ethereum;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    provider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (!provider) return false;
  } else if (!ethereum.isCoinbaseWallet) {
    return false;
  }
  
  // Check 1: Basic flag (must be true)
  if (!provider.isCoinbaseWallet) {
    return false;
  }
  
  // Check 2: host property must include 'coinbase.com'
  if (typeof provider.host !== "string" || !provider.host.includes("coinbase.com")) {
    return false;
  }
  
  // Check 3: jsonRpcUrl property must include 'coinbase.com'
  if (typeof provider.jsonRpcUrl !== "string" || !provider.jsonRpcUrl.includes("coinbase.com")) {
    return false;
  }
  
  // Check 4: overridesMetaMask should be false (genuine doesn't override MetaMask)
  if (provider.overridesMetaMask !== false && provider.overridesMetaMask !== undefined) {
    return false;
  }
  
  // Check 5: isWalletLink flag (legacy, true in genuine installs)
  if (!provider.isWalletLink) {
    return false;
  }
  
  // Check 6: CoinbaseWalletProvider class exists
  if (typeof (window as any).CoinbaseWalletProvider !== "function") {
    return false;
  }
  
  // Check 7: WalletLinkProvider class exists (legacy)
  if (typeof (window as any).WalletLinkProvider !== "function") {
    return false;
  }
  
  // Check 8: Behavioral check - Test a safe RPC call (eth_chainId)
  try {
    const chainId = await provider.request({ method: "eth_chainId" });
    if (typeof chainId !== "string" || !chainId.startsWith("0x")) {
      return false; // Invalid response format
    }
  } catch (error) {
    console.error("Coinbase Wallet RPC test failed:", error);
    return false; // Fakes may error or respond oddly
  }
  
  return true;
}

/**
 * Synchronous version of Coinbase Wallet detection (without behavioral check)
 * Use this for quick checks, but prefer the async version for final verification
 */
export function isCoinbaseWalletProviderAvailableSync(): boolean {
  if (typeof window === "undefined") return false;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return false;
  
  let provider = ethereum;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    provider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (!provider) return false;
  } else if (!ethereum.isCoinbaseWallet) {
    return false;
  }
  
  // Check 1: Basic flag (must be true)
  if (!provider.isCoinbaseWallet) {
    return false;
  }
  
  // Check 2: host property must include 'coinbase.com'
  if (typeof provider.host !== "string" || !provider.host.includes("coinbase.com")) {
    return false;
  }
  
  // Check 3: jsonRpcUrl property must include 'coinbase.com'
  if (typeof provider.jsonRpcUrl !== "string" || !provider.jsonRpcUrl.includes("coinbase.com")) {
    return false;
  }
  
  // Check 4: overridesMetaMask should be false (genuine doesn't override MetaMask)
  if (provider.overridesMetaMask !== false && provider.overridesMetaMask !== undefined) {
    return false;
  }
  
  // Check 5: isWalletLink flag (legacy, true in genuine installs)
  if (!provider.isWalletLink) {
    return false;
  }
  
  // Check 6: CoinbaseWalletProvider class exists
  if (typeof (window as any).CoinbaseWalletProvider !== "function") {
    return false;
  }
  
  // Check 7: WalletLinkProvider class exists (legacy)
  if (typeof (window as any).WalletLinkProvider !== "function") {
    return false;
  }
  
  return true;
}

/**
 * Get Coinbase Wallet Ethereum provider
 * Returns the provider object if available, null otherwise
 */
export function getCoinbaseWalletProvider(): EthereumProvider | null {
  if (typeof window === "undefined") return null;
  
  const ethereum = (window as any).ethereum;
  if (!ethereum) return null;
  
  // Handle multiple providers (e.g., MetaMask + Coinbase)
  if (Array.isArray(ethereum.providers)) {
    const coinbaseProvider = ethereum.providers.find((p: any) => p.isCoinbaseWallet);
    if (coinbaseProvider && typeof coinbaseProvider.request === "function") {
      return coinbaseProvider as EthereumProvider;
    }
  } else if (ethereum.isCoinbaseWallet && typeof ethereum.request === "function") {
    return ethereum as EthereumProvider;
  }
  
  return null;
}

/**
 * Check if Coinbase Wallet Solana provider is available
 * Based on coinbase-reqsol.txt analysis:
 * - window.coinbaseSolana exists directly (created at line 13616)
 * - window.coinbaseSolana._storage.scope === 'coinbaseSolana' (storage uses underscore prefix, line 13623)
 * - Has methods: connect(), disconnect(), signMessage(), signTransaction(), sendTransaction()
 * 
 * NOTE: Coinbase Wallet does NOT set window.solana or window.solana.isCoinbaseWallet
 * It only creates window.coinbaseSolana directly
 */
export function isCoinbaseWalletSolanaAvailable(): boolean {
  if (typeof window === "undefined") return false;
  
  const coinbaseSolana = (window as any).coinbaseSolana;
  
  // Primary detection: Check for window.coinbaseSolana directly
  // Coinbase Wallet creates this object directly, not via window.solana
  if (coinbaseSolana && typeof coinbaseSolana === "object") {
    // Verify it's actually Coinbase Wallet by checking storage scope
    // Storage uses underscore prefix: _storage
    // From coinbase-reqsol.txt line 13623: this._storage = new e.ScopedLocalStorage("coinbaseSolana")
    const storage = coinbaseSolana._storage || coinbaseSolana.storage;
    if (storage && storage.scope === "coinbaseSolana") {
      return true;
    }
    
    // If storage check fails but coinbaseSolana exists with expected methods, still consider it valid
    // From coinbase-reqsol.txt: the object has connect, disconnect, signMessage, signTransaction methods
    if (
      typeof coinbaseSolana.connect === "function" &&
      typeof coinbaseSolana.disconnect === "function" &&
      typeof coinbaseSolana.signMessage === "function" &&
      typeof coinbaseSolana.signTransaction === "function"
    ) {
      return true;
    }
  }
  
  return false;
}

/**
 * Wait for Coinbase Wallet to become available (inpage detection with polling)
 * Handles timing issues where Coinbase extension injects window.ethereum asynchronously
 * 
 * @param timeout - Maximum time to wait in milliseconds (default: 3000ms)
 * @param interval - Polling interval in milliseconds (default: 100ms)
 * @returns Promise that resolves to true if Coinbase Wallet is detected, false if timeout
 */
export async function waitForCoinbaseWallet(
  timeout: number = 3000,
  interval: number = 100
): Promise<boolean> {
  if (typeof window === "undefined") return false;
  
  // Immediate check
  if (isCoinbaseWalletProviderAvailableSync()) {
    return true;
  }
  
  // Wait for document to be ready (if not already)
  if (typeof document !== "undefined" && document.readyState !== "complete") {
    await new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const checkReady = () => {
          if (document.readyState === "complete") {
            document.removeEventListener("readystatechange", checkReady);
            resolve();
          }
        };
        document.addEventListener("readystatechange", checkReady);
      }
    });
    
    // Check again after document is ready
    if (isCoinbaseWalletProviderAvailableSync()) {
      return true;
    }
  }
  
  // Polling-based detection (handles async injection timing)
  return new Promise((resolve) => {
    const startTime = Date.now();
    let attempts = 0;
    const maxAttempts = Math.ceil(timeout / interval);
    
    const poll = () => {
      attempts++;
      
      // Check for Coinbase Wallet
      if (isCoinbaseWalletProviderAvailableSync()) {
        resolve(true);
        return;
      }
      
      // Timeout check
      if (Date.now() - startTime >= timeout || attempts >= maxAttempts) {
        resolve(false);
        return;
      }
      
      // Continue polling
      setTimeout(poll, interval);
    };
    
    poll();
  });
}

// ============================================================================
// MAIN DETECTION FUNCTIONS
// ============================================================================

/**
 * Detects which supported wallet is available
 * Returns 'metamask', 'phantom', 'onekey', 'okx', 'binance', 'bitget', 'tokenpocket', 'coinbase', 'rainbow', or null
 * 
 * IMPORTANT: Check order matters! 
 * - OneKey must be checked first (provides backward compatibility interfaces)
 * - OKX must be checked before MetaMask (may inject window.ethereum)
 * - Bitget must be checked before Binance and MetaMask (multi-chain wallet)
 * - TokenPocket must be checked before Binance and MetaMask (multi-chain wallet)
 * - Coinbase must be checked before MetaMask (may inject window.ethereum)
 * - Binance must be checked before MetaMask
 * 
 * Smart Detection Heuristic:
 * - If OKX is detected for both Solana AND Bitcoin, then Ethereum provider is very likely OKX
 *   (OKX is a multi-chain wallet, so if Solana and Bitcoin are both OKX, Ethereum must be too)
 * - Bitget uses unique window.bitkeep namespace, so it's easy to detect
 * - TokenPocket uses window.ethereum.isTokenPocket flag (official detection method)
 * - Coinbase uses robust multi-check detection (host, jsonRpcUrl, isWalletLink, etc.)
 */
export function detectSupportedWallet(): SupportedWallet | null {
  if (typeof window === "undefined") return null;

  // Check for Phantom first (most specific, but OneKey masquerades as Phantom)
  if (window.phantom?.ethereum || window.ethereum?.isPhantom) {
    // But exclude OneKey (OneKey provides window.phantom for backward compatibility)
    if (!(window.$onekey || window.ethereum?.isOneKey)) {
      return "phantom";
    }
  }

  // Check for OneKey (highest priority - provides backward compatibility)
  // OneKey provides window.phantom, window.unisat, etc. for backward compatibility
  if (window.$onekey?.ethereum || window.ethereum?.isOneKey || (window as any).$onekey) {
    return "onekey";
  }

  // Smart OKX Detection Heuristic:
  // If OKX Solana AND Bitcoin are both detected, then Ethereum is very likely OKX
  // (OKX is a multi-chain wallet - if both Solana and Bitcoin are OKX, Ethereum must be too)
  const hasOKXSolana = !!(window as any).okxwallet?.solana;
  const hasOKXBitcoin = !!(window as any).okxwallet?.bitcoin;
  const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;
  
  // If both Solana and Bitcoin are OKX, then Ethereum is definitely OKX
  if (hasOKXMultiChain && window.ethereum) {
    return "okx";
  }

  // Check for OKX (must be before Bitget, TokenPocket, Binance, and MetaMask!)
  // OKX can be detected via:
  // 1. window.okxwallet.ethereum (primary method)
  // 2. window.ethereum.isOKX (if OKX sets this flag)
  // 3. window.okxwallet exists (even if ethereum provider not immediately available)
  if (window.okxwallet?.ethereum || (window as any).ethereum?.isOKX) {
    return "okx";
  }
  
  // Additional check: if okxwallet exists at all, it's likely OKX
  // (even if ethereum provider isn't available yet, e.g., wallet switched to Bitcoin)
  if ((window as any).okxwallet) {
    return "okx";
  }

  // Check for Bitget (must be before TokenPocket, Binance, and MetaMask!)
  // Bitget uses unique window.bitkeep namespace, so it's easy to detect
  if (isBitgetProviderAvailable()) {
    return "bitget";
  }

  // Check for TokenPocket (must be before Coinbase, Binance, and MetaMask!)
  // TokenPocket uses window.ethereum.isTokenPocket flag (official detection method)
  if (isTokenPocketProviderAvailable()) {
    return "tokenpocket";
  }

  // Check for Rainbow Wallet (must be before Coinbase, Binance, and MetaMask!)
  // Rainbow Wallet injects window.ethereum with isRainbow flag
  const ethereum = window.ethereum as any;
  if (ethereum && ethereum.isRainbow === true) {
    return "rainbow";
  }

  // Check for Coinbase Wallet (must be before Binance and MetaMask!)
  // Uses robust multi-check detection (host, jsonRpcUrl, isWalletLink, etc.)
  if (isCoinbaseWalletProviderAvailableSync()) {
    return "coinbase";
  }

  // Check for Binance (must be before MetaMask!)
  // Uses inpage detection with multiple fallback methods
  if (isBinanceProviderAvailable()) {
    return "binance";
  }

  // Check for MetaMask (must be after OKX, Bitget, TokenPocket, Coinbase, Binance, and Rainbow checks!)
  // Use comprehensive detection to ensure it's truly MetaMask
  if (isMetaMaskProviderAvailable()) {
    return "metamask";
  }

  // If ethereum exists but no specific wallet detected, check if OKX, Bitget, TokenPocket, Coinbase, or Binance exists
  // If OKX exists, it's likely OKX (even if ethereum provider not available)
  if (window.ethereum) {
    // Check Bitget first (unique namespace)
    if (isBitgetProviderAvailable()) {
      return "bitget";
    }
    // Check TokenPocket (uses isTokenPocket flag)
    if (isTokenPocketProviderAvailable()) {
      return "tokenpocket";
    }
    // Check Coinbase Wallet (robust detection)
    if (isCoinbaseWalletProviderAvailableSync()) {
      return "coinbase";
    }
    // Check Rainbow Wallet
    if (window.ethereum && (window.ethereum as any).isRainbow === true) {
      return "rainbow";
    }
    // Only assume MetaMask if comprehensive check passes and other wallets are definitely not present
    const isBinanceDetected = isBinanceProviderAvailable();
    const isCoinbaseDetected = isCoinbaseWalletProviderAvailableSync();
    const isRainbowDetected = window.ethereum && (window.ethereum as any).isRainbow === true;
    if (isMetaMaskProviderAvailable() && !(window as any).okxwallet && !isBinanceDetected && !isCoinbaseDetected && !isRainbowDetected) {
      return "metamask";
    }
    // If okxwallet exists, it's OKX
    if ((window as any).okxwallet) {
      return "okx";
    }
    // If Binance detected, it's Binance
    if (isBinanceDetected) {
      return "binance";
    }
    // If Coinbase detected, it's Coinbase
    if (isCoinbaseDetected) {
      return "coinbase";
    }
  }

  return null;
}

/**
 * Validates if the connected wallet is one of the supported wallets
 */
export function isSupportedWallet(
  wallet: string | null,
): wallet is SupportedWallet {
  return wallet === "metamask" || wallet === "phantom" || wallet === "onekey" || wallet === "okx" || wallet === "binance" || wallet === "bitget" || wallet === "tokenpocket" || wallet === "coinbase" || wallet === "rainbow";
}

// ============================================================================
// CHAIN-AWARE WALLET DETECTION
// ============================================================================

/**
 * Determines the best wallet to connect to based on chain type and availability.
 * Returns the wallet name (not the handler) so components can map it to their own handlers.
 * 
 * Priority order:
 * - Ethereum: OneKey > OKX > Bitget > TokenPocket > Coinbase Wallet > Rainbow > Binance > MetaMask > Phantom
 * - Solana: Phantom > OneKey > OKX > Bitget > TokenPocket > Coinbase Wallet > Binance
 * - Bitcoin: OneKey > OKX > Bitget > Preferred Bitcoin wallet (Unisat/Xverse)
 * 
 * @param chainType - The chain type to detect wallets for
 * @returns The best available wallet name, or null if no wallet is available
 */
export function getBestWallet(chainType: ChainType): WalletName {
  if (chainType === "ethereum") {
    // Priority: OneKey > OKX > Bitget > TokenPocket > Coinbase Wallet > Rainbow > Binance > MetaMask > Phantom
    // OneKey provides backward compatibility (window.phantom, window.unisat) so must be checked first
    if (isOneKeyAvailable()) {
      return "OneKey";
    }

    // Smart Detection Heuristic: If OKX Solana AND Bitcoin are both detected,
    // then Ethereum is very likely OKX (OKX is a multi-chain wallet)
    const hasOKXSolana = isOKXSolanaSDKAvailable();
    const hasOKXBitcoin = isOKXBitcoinSDKAvailable();
    const hasOKXMultiChain = hasOKXSolana && hasOKXBitcoin;

    // Check OKX (if detected via multi-chain heuristic OR standard detection)
    // Check OKX after OneKey because OneKey might inject window.ethereum
    if (hasOKXMultiChain || isOKXEthereumSDKAvailable()) {
      return "OKX";
    }

    // Check Bitget (must be before TokenPocket, Binance, and MetaMask)
    if (isBitgetProviderAvailable()) {
      return "Bitget";
    }

    // Check TokenPocket Ethereum (must be before Coinbase, Binance, and MetaMask)
    // TokenPocket is a multi-chain wallet - check Ethereum support specifically
    if (isTokenPocketEthereumAvailable()) {
      return "TokenPocket";
    }

    // Check Coinbase Wallet (must be before Rainbow, Binance, and MetaMask)
    // Coinbase Wallet uses robust multi-check detection
    if (isCoinbaseWalletProviderAvailableSync()) {
      return "Coinbase Wallet";
    }

    // Check Rainbow Wallet (must be before Binance and MetaMask)
    if (isRainbowAvailable()) {
      return "Rainbow";
    }

    // Check Binance (must be before MetaMask)
    if (isBinanceAvailable()) {
      return "Binance";
    }

    // Only show MetaMask if comprehensive detection confirms it's truly MetaMask
    // The isMetaMaskProviderAvailable() function already excludes OKX, Bitget, TokenPocket, Coinbase, Binance, OneKey, and Phantom
    if (isMetaMaskProviderAvailable()) {
      return "MetaMask";
    }

    if (isPhantomAvailable()) {
      return "Phantom";
    }
  } else if (chainType === "solana") {
    // Priority: Phantom > OneKey > OKX > Bitget > TokenPocket > Coinbase Wallet > Binance
    if (isPhantomAvailable()) {
      return "Phantom";
    }

    if (isOneKeySolanaSDKAvailable()) {
      return "OneKey";
    }

    if (isOKXSolanaSDKAvailable()) {
      return "OKX";
    }

    if (isBitgetSolanaAvailable()) {
      return "Bitget";
    }

    // Check TokenPocket Solana support
    if (isTokenPocketSolanaAvailable()) {
      return "TokenPocket";
    }

    // Check Coinbase Wallet Solana support
    if (isCoinbaseWalletSolanaAvailable()) {
      return "Coinbase Wallet";
    }

    if (isBinanceSolanaAvailable()) {
      return "Binance";
    }
  } else if (chainType === "bitcoin") {
    // Priority: OneKey > OKX > Bitget > Preferred Bitcoin wallet (Unisat/Xverse)
    if (isOneKeyBitcoinSDKAvailable()) {
      return "OneKey";
    }

    if (isOKXBitcoinSDKAvailable()) {
      return "OKX";
    }

    // Check if Bitget Bitcoin (unisat) is available
    if (isBitgetBitcoinAvailable()) {
      return "Bitget";
    }

    if (isBitcoinWalletAvailable()) {
      return "Bitcoin Wallet";
    }
  }

  return null;
}

/**
 * Simple wallet detection for basic use cases (like FunctionalSigner).
 * Returns the detected wallet name without chain type consideration.
 * Priority: OneKey > Phantom > MetaMask
 */
export function detectWallet(): WalletName {
  if (typeof window === "undefined") return null;

  // Check OneKey first (provides backward compatibility)
  if (isOneKeyAvailable()) {
    return "OneKey";
  }

  // Check Phantom (but exclude OneKey masquerading as Phantom)
  if (isPhantomAvailable()) {
    return "Phantom";
  }

  // Check MetaMask (comprehensive detection)
  if (isMetaMaskProviderAvailable()) {
    return "MetaMask";
  }

  return null;
}
